import { z } from 'zod';
import { RecordIdSchema } from './identifiers';
import { DecimalStringSchema, MoneySchema } from './money';
import { BusinessDateSchema, InstantSchema } from './time';

/**
 * Receivables — the financial side of a contract after its schedule (BMP-2 package 2):
 * `COL-RECEIPT-002 … 006`, `COL-STATEMENT-001/002`, `COL-REMIND-001`, `FIN-REPORT-003`.
 *
 * The contractual schedule (sales) and the settlement against it (here) stay separate: a waiver, a
 * credit or a late fee never edits an instalment's contractual amount. Every policy value is a setting
 * that is *not configured* until decided — overpayment handling (`BD-38`), grace (`BD-11`), late fees
 * (`BD-12`) — and an unconfigured policy refuses or does nothing; it is never guessed.
 */

const IdempotencyKeySchema = z.string().trim().min(8).max(200);
const ReasonSchema = z.string().trim().min(3).max(500);

/* ---------------------------------------------------------------- customer credit */

/** A movement on a customer's unapplied credit for one contract. Append-only. */
export const CREDIT_MOVEMENT_KINDS = [
  'overpayment',
  'applied',
  'chargeSettled',
  'refunded',
  'reversed',
  /** A credit refund whose payment came back (reversed in treasury): the credit is the customer's again. */
  'refundReturned',
] as const;
export const CreditMovementKindSchema = z.enum(CREDIT_MOVEMENT_KINDS);
export type CreditMovementKind = z.infer<typeof CreditMovementKindSchema>;

/** Kinds that add to the balance; the others take from it. */
export const CREDIT_INFLOWS: readonly CreditMovementKind[] = ['overpayment', 'refundReturned'];

export const CreditMovementSchema = z.strictObject({
  movementId: RecordIdSchema,
  customerId: RecordIdSchema,
  contractId: RecordIdSchema,
  kind: CreditMovementKindSchema,
  amount: MoneySchema,
  source: z.strictObject({ type: z.string(), id: z.string() }),
  createdByAccountId: z.string(),
  createdAt: InstantSchema,
});
export type CreditMovement = z.infer<typeof CreditMovementSchema>;

export const ContractCreditSchema = z.strictObject({
  contractId: RecordIdSchema,
  customerId: RecordIdSchema,
  balance: MoneySchema,
  movements: z.array(CreditMovementSchema),
});
export type ContractCredit = z.infer<typeof ContractCreditSchema>;

/** Apply credit to the contract's open instalments, oldest first (BD-13 proposed rule). */
export const ApplyCreditSchema = z.strictObject({
  amount: MoneySchema.optional(),
  idempotencyKey: IdempotencyKeySchema,
});
export type ApplyCredit = z.infer<typeof ApplyCreditSchema>;

/* ---------------------------------------------------------------- charges (late fees) */

/** `changeFee`: the configured fee of a unit replacement or assignment (BD-07, BD-08). */
export const CHARGE_KINDS = ['lateFee', 'changeFee'] as const;
export const CHARGE_STATES = ['open', 'settled', 'waived'] as const;
export const ChargeStateSchema = z.enum(CHARGE_STATES);

export const ChargeSchema = z.strictObject({
  chargeId: RecordIdSchema,
  kind: z.enum(CHARGE_KINDS),
  /** The instalment a late fee is for; for a change fee, the contract change it charges. */
  installmentId: RecordIdSchema,
  contractId: RecordIdSchema,
  customerId: RecordIdSchema,
  /** The month the fee is for, `YYYY-MM`; one fee per instalment per month. */
  period: z.string().regex(/^\d{4}-\d{2}$/),
  amount: MoneySchema,
  settledAmount: MoneySchema,
  waivedAmount: MoneySchema,
  outstanding: MoneySchema,
  state: ChargeStateSchema,
  createdAt: InstantSchema,
});
export type Charge = z.infer<typeof ChargeSchema>;

export const SettleChargeSchema = z.strictObject({
  idempotencyKey: IdempotencyKeySchema,
});

/* ---------------------------------------------------------------- waivers */

export const WAIVER_TARGETS = ['installment', 'charge'] as const;
export const WAIVER_STATES = ['pendingApproval', 'applied', 'rejected'] as const;
export const WaiverStateSchema = z.enum(WAIVER_STATES);

export const WaiverSchema = z.strictObject({
  waiverId: RecordIdSchema,
  target: z.strictObject({ kind: z.enum(WAIVER_TARGETS), id: RecordIdSchema }),
  contractId: RecordIdSchema,
  customerId: RecordIdSchema,
  amount: MoneySchema,
  reason: z.string(),
  state: WaiverStateSchema,
  approvalRequestId: z.string().optional(),
  requestedByAccountId: z.string(),
  decidedAt: InstantSchema.optional(),
  createdAt: InstantSchema,
});
export type Waiver = z.infer<typeof WaiverSchema>;

export const RequestWaiverSchema = z.strictObject({
  target: z.strictObject({ kind: z.enum(WAIVER_TARGETS), id: RecordIdSchema }),
  amount: MoneySchema,
  reason: ReasonSchema,
  idempotencyKey: IdempotencyKeySchema,
});
export type RequestWaiver = z.infer<typeof RequestWaiverSchema>;

/* ---------------------------------------------------------------- refunds */

/**
 * `reservation`: the deposit received on a reservation that ended before its contract (cancelled,
 * expired, rejected) — owed back to the customer, created with the ending, in its transaction.
 */
export const REFUND_SOURCES = ['credit', 'cancellation', 'reservation'] as const;
export const RefundSourceSchema = z.enum(REFUND_SOURCES);
/**
 * `partiallyPaid`: treasury executed payments for part of the amount; the rest is still owed. A refund
 * is paid through treasury payment requests (BD-59) whose execution settles it.
 */
export const REFUND_STATES = [
  'pendingApproval',
  'approved',
  'partiallyPaid',
  'paid',
  'rejected',
  'cancelled',
] as const;
export const RefundStateSchema = z.enum(REFUND_STATES);
export type RefundState = z.infer<typeof RefundStateSchema>;

/** How a refund was paid. A reference only: the product never executes a payment (ADR-0034 §5). */
export const REFUND_METHODS = ['cash', 'bankTransfer', 'cheque'] as const;

export const RefundSchema = z.strictObject({
  refundId: RecordIdSchema,
  refundNumber: z.string().optional(),
  source: RefundSourceSchema,
  /** Absent only on a reservation refund: the money was taken before any contract existed. */
  contractId: RecordIdSchema.optional(),
  reservationId: RecordIdSchema.optional(),
  customerId: RecordIdSchema,
  amount: MoneySchema,
  /** Paid so far through executed treasury payments. */
  paidAmount: MoneySchema,
  /** Asked of treasury and not yet executed, rejected or cancelled. */
  inFlightAmount: MoneySchema,
  /** What may still be asked of treasury: amount − paid − in flight. */
  requestableAmount: MoneySchema,
  /** The treasury payment requests made for this refund, newest first. */
  payments: z.array(
    z.strictObject({
      paymentRequestId: RecordIdSchema,
      state: z.string(),
      amount: MoneySchema,
      executedOn: BusinessDateSchema.optional(),
    }),
  ),
  reason: z.string(),
  state: RefundStateSchema,
  approvalRequestId: z.string().optional(),
  requestedByAccountId: z.string(),
  payment: z
    .strictObject({
      method: z.enum(REFUND_METHODS),
      paidOn: BusinessDateSchema,
      reference: z.string(),
      paidByAccountId: z.string(),
    })
    .optional(),
  cancellationReason: z.string().optional(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Refund = z.infer<typeof RefundSchema>;

export const RequestRefundSchema = z.strictObject({
  contractId: RecordIdSchema,
  amount: MoneySchema,
  reason: ReasonSchema,
  idempotencyKey: IdempotencyKeySchema,
});
export type RequestRefund = z.infer<typeof RequestRefundSchema>;

export const PayRefundSchema = z.strictObject({
  method: z.enum(REFUND_METHODS),
  paidOn: BusinessDateSchema,
  /** The bank's or the cheque's own reference, as given. Never reformatted. */
  reference: z.string().trim().min(1).max(120),
  expectedVersion: z.number().int().positive(),
});
export type PayRefund = z.infer<typeof PayRefundSchema>;

/**
 * Ask treasury to pay (part of) an approved refund (BD-59): a payment request whose execution settles
 * the refund. The payee is the customer's: an existing payee (required for a bank transfer, with
 * approved bank details) or, for cash and cheques, one created in the customer's name.
 */
export const RequestRefundPaymentSchema = z.strictObject({
  fromAccountId: RecordIdSchema,
  method: z.enum(REFUND_METHODS),
  amount: MoneySchema,
  payeeId: RecordIdSchema.optional(),
  idempotencyKey: IdempotencyKeySchema,
});
export type RequestRefundPayment = z.infer<typeof RequestRefundPaymentSchema>;

export const CancelRefundSchema = z.strictObject({
  reason: ReasonSchema,
  expectedVersion: z.number().int().positive(),
});

export const RefundQuerySchema = z.strictObject({
  state: RefundStateSchema.optional(),
  contractId: RecordIdSchema.optional(),
  reservationId: RecordIdSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().min(1).max(200).optional(),
});
export type RefundQuery = z.infer<typeof RefundQuerySchema>;

export const RefundPageSchema = z.strictObject({
  items: z.array(RefundSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  nextCursor: z.string().optional(),
});

/* ---------------------------------------------------------------- collector and notes */

export const AssignCollectorSchema = z.strictObject({
  collectorAccountId: z.string().min(1).max(200),
  reason: ReasonSchema,
});
export type AssignCollector = z.infer<typeof AssignCollectorSchema>;

export const CollectionNoteSchema = z.strictObject({
  noteId: RecordIdSchema,
  contractId: RecordIdSchema,
  text: z.string(),
  /** A follow-up task opened with the note, if any. */
  taskId: z.string().optional(),
  createdByAccountId: z.string(),
  createdAt: InstantSchema,
});
export type CollectionNote = z.infer<typeof CollectionNoteSchema>;

export const AddCollectionNoteSchema = z.strictObject({
  text: z.string().trim().min(2).max(2000),
  /** Open a follow-up task for the collector, due on this date. */
  followUpOn: BusinessDateSchema.optional(),
});
export type AddCollectionNote = z.infer<typeof AddCollectionNoteSchema>;

/* ---------------------------------------------------------------- statements and ageing */

export const AGEING_BUCKETS = [
  'current',
  'days1to30',
  'days31to60',
  'days61to90',
  'over90',
] as const;
export type AgeingBucket = (typeof AGEING_BUCKETS)[number];
const BucketsSchema = z.strictObject(
  Object.fromEntries(AGEING_BUCKETS.map((bucket) => [bucket, DecimalStringSchema])) as Record<
    AgeingBucket,
    typeof DecimalStringSchema
  >,
);

export const StatementRowSchema = z.strictObject({
  installmentId: RecordIdSchema,
  sequence: z.number().int(),
  kind: z.string(),
  dueOn: BusinessDateSchema,
  amount: DecimalStringSchema,
  paid: DecimalStringSchema,
  waived: DecimalStringSchema,
  remaining: DecimalStringSchema,
  state: z.string(),
  /** Days past due after the configured grace (`BD-11`); 0 when not overdue. */
  daysOverdue: z.number().int().nonnegative(),
  overdue: z.boolean(),
});
export type StatementRow = z.infer<typeof StatementRowSchema>;

export const ContractStatementSchema = z.strictObject({
  meta: z.strictObject({
    generatedAt: InstantSchema,
    asOf: BusinessDateSchema,
    currency: z.string(),
    /** `null` when `collections.gracePeriodDays` is not configured: overdue counts from the due date. */
    graceDays: z.number().int().nullable(),
  }),
  contractId: RecordIdSchema,
  contractNumber: z.string(),
  customerId: RecordIdSchema,
  collectorAccountId: z.string().optional(),
  rows: z.array(StatementRowSchema),
  receipts: z.array(
    z.strictObject({
      receiptId: RecordIdSchema,
      receiptNumber: z.string(),
      receivedOn: BusinessDateSchema,
      amount: DecimalStringSchema,
      unapplied: DecimalStringSchema,
      state: z.string(),
    }),
  ),
  charges: z.array(ChargeSchema),
  credit: DecimalStringSchema,
  totals: z.strictObject({
    scheduled: DecimalStringSchema,
    paid: DecimalStringSchema,
    waived: DecimalStringSchema,
    outstanding: DecimalStringSchema,
    overdue: DecimalStringSchema,
    chargesOutstanding: DecimalStringSchema,
  }),
  ageing: BucketsSchema,
});
export type ContractStatement = z.infer<typeof ContractStatementSchema>;

export const AgeingQuerySchema = z.strictObject({
  asOf: BusinessDateSchema.optional(),
  projectId: RecordIdSchema.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type AgeingQuery = z.infer<typeof AgeingQuerySchema>;

export const AgeingReportSchema = z.strictObject({
  meta: z.strictObject({
    generatedAt: InstantSchema,
    asOf: BusinessDateSchema,
    graceDays: z.number().int().nullable(),
    /** True when more contracts carry balances than rows were returned; totals still cover all. */
    truncated: z.boolean(),
  }),
  currencies: z.array(
    z.strictObject({
      currency: z.string(),
      rows: z.array(
        z.strictObject({
          contractId: RecordIdSchema,
          contractNumber: z.string(),
          customerId: RecordIdSchema,
          buckets: BucketsSchema,
          total: DecimalStringSchema,
        }),
      ),
      totals: BucketsSchema,
      total: DecimalStringSchema,
      collectedInPeriod: DecimalStringSchema,
    }),
  ),
});
export type AgeingReport = z.infer<typeof AgeingReportSchema>;

export const ForecastQuerySchema = z
  .strictObject({
    from: BusinessDateSchema,
    to: BusinessDateSchema,
    projectId: RecordIdSchema.optional(),
  })
  .refine((value) => value.from <= value.to, { message: 'RANGE_REVERSED' });
export type ForecastQuery = z.infer<typeof ForecastQuerySchema>;

export const ForecastSchema = z.strictObject({
  meta: z.strictObject({ generatedAt: InstantSchema }),
  months: z.array(
    z.strictObject({
      month: z.string(),
      currency: z.string(),
      expected: DecimalStringSchema,
      instalments: z.number().int().nonnegative(),
    }),
  ),
});
export type Forecast = z.infer<typeof ForecastSchema>;

/* ---------------------------------------------------------------- vocabulary */

export const RECEIVABLE_APPROVAL_OPERATIONS = {
  waiver: 'collection.waiver',
  refund: 'collection.refund',
} as const;

export const RECEIVABLE_AUDIT_ACTIONS = {
  creditRecorded: 'collection.credit.recorded',
  creditApplied: 'collection.credit.applied',
  chargeCreated: 'collection.charge.created',
  chargeSettled: 'collection.charge.settled',
  waiverRequested: 'collection.waiver.requested',
  waiverApplied: 'collection.waiver.applied',
  waiverRejected: 'collection.waiver.rejected',
  refundRequested: 'collection.refund.requested',
  refundApproved: 'collection.refund.approved',
  refundRejected: 'collection.refund.rejected',
  refundPaid: 'collection.refund.paid',
  refundPaymentReturned: 'collection.refund.paymentReturned',
  /** Treasury was asked to pay (part of) a refund (BD-59). */
  refundPaymentRequested: 'collection.refund.paymentRequested',
  refundCancelled: 'collection.refund.cancelled',
  collectorAssigned: 'collection.collector.assigned',
  noteAdded: 'collection.note.added',
  overdueNotified: 'collection.overdue.notified',
} as const;
